import { fetchEventSource } from "@microsoft/fetch-event-source";
import { HttpClient, Result, SSEEvent } from "./HttpClient.ts";
import { AuthService } from "../auth/AuthService.ts";

export type FetchLike = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response>;

export type FetchEventSourceLike = typeof fetchEventSource;

export class FetchHttpClient implements HttpClient {
  private token: string | null = null;

  constructor(
    private readonly fetch: FetchLike,
    private readonly fetchEventSource: FetchEventSourceLike,
    private readonly baseUrl: string,
    private readonly authService: AuthService,
  ) {}

  setToken(token: string | null) {
    this.token = token;
  }

  async get<T>(path: string, query?: Record<string, string>): Result<T> {
    return this.executeRequest<T>(async () =>
      this.fetch(this.getFullUrl(path, query), {
        headers: await this.getHeaders(),
      }),
    );
  }

  async getRaw(path: string, query: Record<string, string>): Promise<Response> {
    return this.fetch(this.getFullUrl(path, query), {
      headers: await this.getHeaders(),
    });
  }

  async post<T>(
    path: string,
    data: BodyInit | JsonValue,
    query?: Record<string, string>,
  ): Result<T> {
    return this.submit<T>(path, data, "POST", query);
  }

  async stream(path: string, data: BodyInit | JsonValue) {
    return this.fetch(this.getFullUrl(path), {
      method: "POST",
      body: typeof data === "string" ? data : JSON.stringify(data),
      headers: { ...(await this.getHeaders()), accept: "text/event-stream" },
    });
  }

  async streamWithCallback(
    path: string,
    data: BodyInit | JsonValue,
    onChunk: (chunk: string) => void,
    onComplete?: () => void,
    onError?: (error: Error) => void,
  ): Promise<void> {
    try {
      const response = await this.executeStreamRequest(path, data);
      await this.processStreamResponse(response, onChunk, onComplete, onError);
    } catch (error) {
      if (onError) {
        onError(error as Error);
      } else {
        throw error;
      }
    }
  }

  async streamWithEventSource(
    path: string,
    data: BodyInit | JsonValue,
    onEvent: (event: SSEEvent) => void,
    onComplete?: () => void,
    onError?: (error: Error) => void,
  ): Promise<void> {
    try {
      const headers = await this.getHeaders();
      const body = typeof data === "string" ? data : JSON.stringify(data);

      await this.fetchEventSource(this.getFullUrl(path), {
        method: "POST",
        headers: { ...headers, accept: "text/event-stream" },
        openWhenHidden: true,
        body,
        onmessage: (event) => {
          onEvent({
            id: event.id,
            event: event.event as SSEEvent["event"],
            data: JSON.parse(event.data) as SSEEvent["data"],
          } as SSEEvent);
        },
        onopen: async (response) => {
          if (response.status === 401) {
            throw new Error("Unauthorized");
          }
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
        },
        onclose: () => {
          onComplete?.();
        },
        onerror: (error) => {
          if (onError) {
            onError(error);
          } else {
            throw error;
          }
        },
      });
    } catch (error) {
      if ((error as Error).message === "Unauthorized") {
        const newToken = await this.authService.refreshToken();
        if (newToken) {
          this.setToken(newToken);
          return this.streamWithEventSource(
            path,
            data,
            onEvent,
            onComplete,
            onError,
          );
        }
      }
      if (onError) {
        onError(error as Error);
      } else {
        throw error;
      }
    }
  }

  async patch<T>(path: string, data: BodyInit | JsonValue): Result<T> {
    return this.submit<T>(path, data, "PATCH");
  }

  async delete(
    path: string,
  ): Promise<{ success: boolean; httpStatus: number }> {
    return await this.executeRequest(
      async () =>
        await this.fetch(this.getFullUrl(path), {
          method: "DELETE",
          headers: await this.getHeaders(),
        }),
    );
  }

  private async submit<T>(
    path: string,
    data: BodyInit | JsonValue,
    method: "POST" | "PATCH",
    query?: Record<string, string>,
  ): Result<T> {
    const body = typeof data === "string" ? data : JSON.stringify(data);
    return this.executeRequest<T>(async () =>
      this.fetch(this.getFullUrl(path, query), {
        method,
        body,
        headers: await this.getHeaders(),
      }),
    );
  }

  private getFullUrl(path: string, query?: Record<string, string>) {
    const url = new URL(this.baseUrl);
    url.pathname = `${url.pathname.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
    return url.toString() + (query ? `?${new URLSearchParams(query)}` : "");
  }

  private async getHeaders(): Promise<{
    "Content-Type": string;
    Authorization: `Bearer ${string}`;
  }> {
    if (!this.token) throw new Error("No token set");
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${this.token}`,
    };
  }

  private async executeRequest<T>(
    requestFn: () => Promise<Response>,
    retryIfFails: boolean = true,
  ): Result<T> {
    try {
      const response = await requestFn();

      if (response.status === 401 && retryIfFails)
        throw new Error("Unauthorized");

      return await this.processResponse<T>(response);
    } catch (error) {
      if (retryIfFails) {
        const newToken = await this.authService.refreshToken();
        if (newToken) {
          this.setToken(newToken);
          return this.executeRequest<T>(requestFn, false);
        }
      }
      throw error;
    }
  }

  private async processResponse<T>(response: Response): Result<T> {
    let data: T | undefined;

    if (response.status !== 204) {
      try {
        data = await response.json();
      } catch (error) {
        console.error("Error parsing JSON response", error);
      }
    }

    return {
      success: response.ok,
      data,
      httpStatus: response.status,
    };
  }

  private async executeStreamRequest(
    path: string,
    data: BodyInit | JsonValue,
    retryIfFails: boolean = true,
  ): Promise<Response> {
    try {
      const headers = await this.getHeaders();
      const response = await this.fetch(this.getFullUrl(path), {
        method: "POST",
        body: typeof data === "string" ? data : JSON.stringify(data),
        headers: { ...headers, accept: "text/event-stream" },
      });

      if (response.status === 401 && retryIfFails) {
        throw new Error("Unauthorized");
      }

      return response;
    } catch (error) {
      if (retryIfFails && (error as Error).message !== "No token set") {
        const newToken = await this.authService.refreshToken();
        if (newToken) {
          this.setToken(newToken);
          return this.executeStreamRequest(path, data, false);
        }
      }
      throw error;
    }
  }

  private async processStreamResponse(
    response: Response,
    onChunk: (chunk: string) => void,
    onComplete?: () => void,
    onError?: (error: Error) => void,
  ): Promise<void> {
    if (!response.body) {
      const error = new Error("No response body");
      if (onError) {
        onError(error);
        return;
      }
      throw error;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          onComplete?.();
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        onChunk(chunk);
      }
    } catch (error) {
      if (onError) {
        onError(error as Error);
      } else {
        throw error;
      }
    }
  }
}
